import { useEffect, useRef, useState, type ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { Icon } from '../components/installer/icons.js'
import type { ProofStep } from '../lib/proofSteps.js'
import { useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

/**
 * A multi-capture checkpoint captures its sub-shots back-to-back in one viewfinder session
 * (condition scan: front panels → top edges → rear corners) with a flash per capture
 * the opened disconnect cover (fuses, 35 A). Each sub-shot plays its clip (AR box burned in),
 * locks, and auto-captures with a flash; after the third, it analyzes and routes to the 3-photo
 * summary on verify-passed. Rendered by CaptureScreen when the active step has `subShots`.
 */
export const ElectricalCaptureScreen = ({ step }: { step: ProofStep }): ReactElement => {
  const subs = step.subShots ?? []
  const { dispatch } = useDemoStore()
  const { goto } = useStep()

  const [primerVisible, setPrimerVisible] = useState(true)
  const [idx, setIdx] = useState(0)
  const [arLocked, setArLocked] = useState(false)
  const [flashing, setFlashing] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const timers = useRef<number[]>([])
  const push = (id: number): void => void timers.current.push(id)
  const clearTimers = (): void => {
    for (const t of timers.current) clearTimeout(t)
    timers.current = []
  }
  useEffect(() => clearTimers, [])

  // Drive one sub-shot each time `idx` advances (after the primer is dismissed): play the clip,
  // lock the AR box, then flash + advance (or analyze → summary on the last one).
  useEffect(() => {
    if (primerVisible) return
    setArLocked(false)
    videoRef.current?.play().catch(() => {})
    push(window.setTimeout(() => setArLocked(true), 1000))
    push(
      window.setTimeout(() => {
        setFlashing(true)
        push(window.setTimeout(() => setFlashing(false), 120))
        if (idx + 1 < subs.length) {
          push(window.setTimeout(() => setIdx(idx + 1), 340))
        } else {
          push(window.setTimeout(() => setAnalyzing(true), 200))
          push(
            window.setTimeout(() => {
              dispatch({ type: 'MARK_CAPTURED', id: step.id })
              goto('verify-passed')
            }, 1800),
          )
        }
      }, 2600),
    )
    return clearTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, primerVisible])

  const active = subs[idx]
  if (active === undefined) return <AppShell fullbleed>{null}</AppShell>

  const dismissPrimer = (): void => {
    setPrimerVisible(false)
    videoRef.current?.play().catch(() => {})
  }

  return (
    <AppShell fullbleed>
      <div className="installer-surface" style={{ position: 'absolute', inset: 0 }}>
        <div className={`camera-screen ${primerVisible ? 'primer-active' : ''}`}>
          <div className="camera-header">
            <button className="back" onClick={() => goto('proof-bundle')} aria-label="Back">
              <Icon.Back />
            </button>
            <div className="title">{step.label}</div>
            <div className="privacy-badge">
              <Icon.ShieldPlain size={12} />
              Privacy mask on
            </div>
          </div>

          <div className="camera-body">
            <video
              key={active.id}
              ref={videoRef}
              className="camera-bg camera-bg-video"
              src={active.video.src}
              poster={active.video.poster}
              muted
              playsInline
              loop
              preload="auto"
            />

            {/* Primer overlay — lists the three captures for the electrical checkpoint */}
            <div className={`primer-overlay ${primerVisible ? 'visible' : ''}`}>
              <div className="primer-card">
                <div className="primer-preview">
                  {/* One wide instructional guide (disconnect + nameplate together) covers the
                      whole 3-shot checkpoint — never a sub-shot's burned-box poster. */}
                  <div
                    className="primer-preview-scene"
                    style={{ backgroundImage: `url(${step.primerImage || step.basePhoto})` }}
                  >
                    {step.primerBox.map((box) => (
                      <div
                        key={`${box.text}-${box.top}-${box.left}`}
                        className="primer-guide-box"
                        style={{
                          top: box.top,
                          left: box.left,
                          width: box.width,
                          height: box.height,
                        }}
                      >
                        <div className="primer-guide-label">{box.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="primer-title">{step.label}</div>
                <div className="primer-section-label">WHAT TO CAPTURE · 3 SHOTS</div>
                <ul className="primer-list">
                  {step.capture.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <div className="primer-why">
                  <div className="primer-why-label">WHY THIS MATTERS</div>
                  <div className="primer-why-body">{step.why}</div>
                </div>
                <button className="primer-btn" onClick={dismissPrimer}>
                  <Icon.Camera /> Ready to capture
                </button>
              </div>
            </div>

            {/* Shot counter */}
            <div className="framing-prompt locked" style={{ top: 12, bottom: 'auto' }}>
              <Icon.Camera size={14} />
              <span>Capture {idx + 1} of {subs.length}</span>
            </div>

            {/* Framing prompt */}
            <div className={`framing-prompt ${arLocked ? 'locked' : ''}`}>
              <Icon.Clock size={14} />
              <span>{arLocked ? 'Framing locked · capturing…' : 'Hold steady · aligning'}</span>
            </div>

            {/* Analyzing overlay */}
            <div className={`analyzing ${analyzing ? 'visible' : ''}`}>
              <div className="analyzing-spinner" />
              <div className="analyzing-text">Analyzing…</div>
              <div className="analyzing-sub">Cross-checking nameplate, disconnect, and fuse rating</div>
            </div>

            {/* Flash */}
            <div className={`camera-capturing ${flashing ? 'flash' : ''}`} />
          </div>

          <div className="camera-controls">
            <div className="flashlight">
              <Icon.Flash />
            </div>
            <div className="camera-shutter">
              <div className="camera-shutter-inner">
                <Icon.Camera size={22} />
              </div>
            </div>
            <div style={{ width: 36, height: 36 }} />
          </div>
        </div>
      </div>
    </AppShell>
  )
}
